import React, { useState, useEffect, Suspense } from "react";
import Header from "../layouts/Header";
import SideBar from "../layouts/SideBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from "@mui/material/styles";
import { Alert } from "@mui/material";
import { Route, Routes, useNavigate, Navigate } from "react-router-dom";
import { initToken } from "../utils/constant";
import authService from "../services/auth";
import dashboardModuleService from "../services/dashboardModuleService";
import jwtDecode from "jwt-decode";
import LoadingOverlay from "../components/LoadingOverlay";
import { drawerWidth } from "../config/sidebarNav";
import {
  getRoutesForRole,
  getRegisteredModuleKeys,
} from "../config/routeRegistry";
import {
  FALLBACK_CATALOG,
  getDefaultRoute,
  resolveModuleKeys,
} from "../config/rolePermissions";
import { resolveUserRole } from "../utils/resolveUserRole";
import { AuthContext } from "../context/AuthContext";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    height: "100vh",
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
);

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const persistSessionMeta = (token, { role, modules, catalog }) => {
  for (const storage of [window.sessionStorage, window.localStorage]) {
    const loggedJSON = storage.getItem("cblife_logged_admin");
    if (!loggedJSON) continue;

    try {
      const parsed = JSON.parse(loggedJSON);
      if (parsed.token !== token) continue;
      storage.setItem(
        "cblife_logged_admin",
        JSON.stringify({
          ...parsed,
          ...(role !== undefined ? { role } : {}),
          ...(modules !== undefined ? { modules } : {}),
          ...(catalog !== undefined ? { catalog } : {}),
        })
      );
    } catch (_) {
      // ignore malformed session payload
    }
  }
};

const readCachedCatalog = () => {
  const loggedJSON =
    window.sessionStorage.getItem("cblife_logged_admin") ||
    window.localStorage.getItem("cblife_logged_admin");
  if (!loggedJSON) return FALLBACK_CATALOG;
  try {
    const parsed = JSON.parse(loggedJSON);
    return Array.isArray(parsed.catalog) && parsed.catalog.length
      ? parsed.catalog
      : FALLBACK_CATALOG;
  } catch (_) {
    return FALLBACK_CATALOG;
  }
};

export default function Home() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(null);
  const [catalog, setCatalog] = useState(readCachedCatalog);
  const [open, setOpen] = useState(true);
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });

  const homeAlert = (message, isError = false) => {
    setShowAlert({ message: message, isError: isError });
    setTimeout(() => {
      setShowAlert({ message: "", isError: false });
    }, 4000);
  };

  const registeredModuleKeys = getRegisteredModuleKeys(homeAlert);

  useEffect(() => {
    const loggedJSON =
      window.sessionStorage.getItem("cblife_logged_admin") ||
      window.localStorage.getItem("cblife_logged_admin");
    if (loggedJSON) {
      const parsedJSON = JSON.parse(loggedJSON);

      fetchAuth(parsedJSON.token, parsedJSON.role, parsedJSON.catalog).then(
        (user) => {
          if (!user) return;
          const role = resolveUserRole(user);
          const nextCatalog = user.catalog || catalog;
          const modules = resolveModuleKeys(role, user?.modules, nextCatalog);
          initToken(parsedJSON.token, role);
          persistSessionMeta(parsedJSON.token, {
            role,
            modules,
            catalog: nextCatalog,
          });
        }
      );
      if (parsedJSON.token) {
        const decoded = jwtDecode(parsedJSON.token);
        const currentTime = Math.floor(Date.now() / 1000);
        if (decoded?.exp && decoded?.exp < currentTime) {
          window.sessionStorage.removeItem("cblife_logged_admin");
          window.localStorage.removeItem("cblife_logged_admin");
          navigate("/login");
        }
      }
    } else {
      navigate("/login");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAuth = async (token, cachedRole = null, cachedCatalog = null) => {
    try {
      const [authRes, catalogRes] = await Promise.all([
        authService.getAuth(token),
        dashboardModuleService.getCatalog(token).catch(() => null),
      ]);
      const user = authRes.data;
      const nextCatalog =
        catalogRes?.data?.catalog ||
        (Array.isArray(cachedCatalog) && cachedCatalog.length
          ? cachedCatalog
          : FALLBACK_CATALOG);

      setCatalog(nextCatalog);
      setAuth({ ...user, catalog: nextCatalog });

      const role = resolveUserRole(user) || cachedRole;
      const modules = resolveModuleKeys(role, user?.modules, nextCatalog);
      if (role) {
        persistSessionMeta(token, { role, modules, catalog: nextCatalog });
      }
      return { ...user, catalog: nextCatalog };
    } catch (error) {
      if (
        error?.response?.data?.error === "token invalid" ||
        error?.response?.data?.error === "token expired" ||
        error?.status === 403
      ) {
        navigate("/login");
      }
      return null;
    }
  };

  if (!auth) {
    return <LoadingOverlay message="Loading dashboard..." />;
  }

  const role = resolveUserRole(auth);
  const moduleKeys = resolveModuleKeys(role, auth.modules, catalog);
  const allowedRoutes = getRoutesForRole(
    role,
    homeAlert,
    moduleKeys,
    catalog,
    registeredModuleKeys
  );
  const defaultRoute = getDefaultRoute(
    moduleKeys,
    catalog,
    registeredModuleKeys
  );

  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };

  const updateAuth = (data) => {
    setAuth((prev) => ({
      ...prev,
      ...data,
      modules: data.modules ?? prev?.modules,
      catalog: data.catalog ?? prev?.catalog,
    }));
    if (data.catalog) {
      setCatalog(data.catalog);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Header
        open={open}
        handleDrawerOpen={handleDrawerOpen}
        user={auth}
        role={role}
        updateUser={updateAuth}
        homeAlert={homeAlert}
      />
      <SideBar
        handleDrawerClose={handleDrawerClose}
        open={open}
        moduleKeys={moduleKeys}
        catalog={catalog}
        registeredModuleKeys={registeredModuleKeys}
      />
      <Main open={open}>
        <DrawerHeader />
        <Suspense fallback={<LoadingOverlay message="Loading page..." />}>
          <AuthContext.Provider
            value={{ ...auth, role, modules: moduleKeys, catalog }}
          >
            <Routes>
              {allowedRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
              <Route
                path="*"
                element={<Navigate to={defaultRoute} replace />}
              />
            </Routes>
          </AuthContext.Provider>
        </Suspense>
      </Main>
      {showAlert.message && !showAlert.isError && (
        <Alert
          sx={{ position: "fixed", bottom: "1em", right: "1em" }}
          severity="success"
        >
          {showAlert.message}
        </Alert>
      )}
      {showAlert.message && showAlert.isError && (
        <Alert
          sx={{ position: "fixed", bottom: "1em", right: "1em" }}
          severity="warning"
        >
          {showAlert.message}
        </Alert>
      )}
    </Box>
  );
}
